import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Lock, Email, ArrowBack, Refresh, Security, BarChart } from '@mui/icons-material';
import { login, getUserInfo } from '../../services/userService';
import Logo from '../common/Logo';
import SEO from '../common/SEO';

const LoginPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const history = useHistory();

  // Add authentication check on component mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check if user is already authenticated using your existing service
        const response = await getUserInfo();
        
        if (response && response.data) {
          // User is authenticated, redirect to dashboard
          history.push('/home');
          return;
        }
        
        // User not authenticated, stay on login page
      } catch (error) {
        // Error checking authentication
      }
    };

    checkAuth();
  }, [history]);

  const handleLogin = async () => {
    setIsLoading(true);
    setError('');

    try {
      // Use your existing login service
      const response = await login();
      
      if (response && response.data && response.data.authUrl) {
        // Redirect to authorization URL
        window.location.href = response.data.authUrl;
      } else {
        setError('Failed to initiate authentication');
        setIsLoading(false);
      }
    } catch (err) {
      setError('Failed to connect to authentication service');
      setIsLoading(false);
    }
  };

  const handleBackToHome = () => {
    history.push('/');
  };

  return (
    <>
    <SEO
      title="Login"
      description="Log in to GA4.so to access your AI-powered Google Analytics 4 reports, scheduled insights, and team workspaces."
      path="/login"
    />
    <main className="bg-gradient-to-br from-brand-blue-light to-background min-h-screen py-12 font-sans">
      <div className="container-custom px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header with GA4.so branding */}
          <div className="mb-8 flex items-center justify-between">
            <Logo />
            <button 
              onClick={handleBackToHome}
              className="inline-flex items-center text-primary hover:text-brand-pink text-sm font-inter transition-colors"
            >
              <ArrowBack className="mr-2" style={{ fontSize: 14 }} />
              Back to Home
            </button>
          </div>
          
          <div className="bg-card rounded-lg shadow-lg p-8 border border-border text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-blue-light text-primary mb-6">
              <BarChart style={{ fontSize: 28 }} />
            </div>
            
            <h1 className="text-3xl font-bold text-foreground mb-4 font-inter">
              Welcome back
            </h1>
            
            <p className="text-brand-gray-medium mb-8 max-w-lg mx-auto font-dm-sans">
               AI Agent for Google Analytics 4
            </p>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-6 font-dm-sans">
                {error}
              </div>
            )}
            
            <div className="bg-muted rounded-lg p-6 mb-8">
              <button
                onClick={handleLogin}
                disabled={isLoading}
                className="w-full bg-primary hover:opacity-90 disabled:bg-muted-foreground disabled:cursor-not-allowed text-primary-foreground font-medium py-3 px-6 rounded-lg transition-all flex items-center justify-center gap-2 font-inter"
              >
                {isLoading ? (
                  <>
                    <Refresh className="animate-spin" style={{ fontSize: 18 }} />
                    <span className="ml-2">Signing in...</span>
                  </>
                ) : (
                  <>
                    <Lock style={{ fontSize: 18 }} />
                    <span className="ml-2">Sign In</span>
                  </>
                )}
              </button>
            </div>
            
            <div className="text-center border-t border-border pt-6">
              <p className="text-brand-gray-medium font-dm-sans">
                Don't have an account?{' '}
                <button 
                  onClick={() => history.push('/signup')}
                  className="text-primary hover:text-brand-pink font-medium transition-colors cursor-pointer"
                >
                  Sign up
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
    </>
  );
};

export default LoginPage;
